/**
 * Seeded PRNG. The simulation is deterministic given (seed, tickCount, inputs),
 * which buys reproducible bugs, replay tests, and non-diverging chunk
 * fast-forward. Nothing in sim/ or entity/ may call Math.random().
 */

export class Rng {
  private s0 = 0;
  private s1 = 0;
  private s2 = 0;
  private s3 = 0;

  constructor(seed: number | string = 0x1a2b3c4d) {
    this.reseed(seed);
  }

  reseed(seed: number | string): void {
    let h = typeof seed === 'string' ? hashString(seed) : seed >>> 0;
    // splitmix32 to spread a single word into four states
    this.s0 = (h = splitmix32(h));
    this.s1 = (h = splitmix32(h));
    this.s2 = (h = splitmix32(h));
    this.s3 = splitmix32(h);
    // xoshiro degenerates on an all-zero state
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 1;
    for (let i = 0; i < 8; i++) this.u32();
  }

  /** xoshiro128** — fast, good distribution, tiny state. */
  u32(): number {
    const r = Math.imul(this.s1, 5);
    const result = ((r << 7) | (r >>> 25)) >>> 0;
    const t = (this.s1 << 9) >>> 0;
    this.s2 ^= this.s0;
    this.s3 ^= this.s1;
    this.s1 ^= this.s2;
    this.s0 ^= this.s3;
    this.s2 ^= t;
    this.s3 = ((this.s3 << 11) | (this.s3 >>> 21)) >>> 0;
    return Math.imul(result, 9) >>> 0;
  }

  /** [0, 1) */
  float(): number {
    return this.u32() / 4294967296;
  }

  /** [min, max) */
  range(min: number, max: number): number {
    return min + this.float() * (max - min);
  }

  /** Integer in [min, max] inclusive. */
  int(min: number, max: number): number {
    return min + (this.u32() % (max - min + 1));
  }

  /** True with probability p. */
  chance(p: number): boolean {
    return this.float() < p;
  }

  pick<T>(arr: readonly T[]): T {
    return arr[this.u32() % arr.length];
  }

  /** Weighted pick. `weights` must be same length as `arr` and sum > 0. */
  pickWeighted<T>(arr: readonly T[], weights: readonly number[]): T {
    let total = 0;
    for (let i = 0; i < weights.length; i++) total += weights[i];
    let r = this.float() * total;
    for (let i = 0; i < arr.length; i++) {
      r -= weights[i];
      if (r <= 0) return arr[i];
    }
    return arr[arr.length - 1];
  }

  /** In-place Fisher-Yates. */
  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.u32() % (i + 1);
      const t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  /** Sign: -1 or 1. */
  sign(): number {
    return this.u32() & 1 ? 1 : -1;
  }

  /** Gaussian-ish via sum of 3 uniforms. Cheap, adequate for scatter. */
  gaussian(mean = 0, sd = 1): number {
    const u = this.float() + this.float() + this.float() - 1.5;
    return mean + u * sd * 1.1547;
  }

  /** Snapshot/restore for deterministic replay checkpoints. */
  save(): Uint32Array {
    return new Uint32Array([this.s0, this.s1, this.s2, this.s3]);
  }
  load(state: Uint32Array): void {
    this.s0 = state[0];
    this.s1 = state[1];
    this.s2 = state[2];
    this.s3 = state[3];
  }

  /** Derive an independent stream — for per-chunk or per-system generators. */
  fork(salt: number): Rng {
    return new Rng((this.u32() ^ Math.imul(salt, 0x9e3779b9)) >>> 0);
  }
}

function splitmix32(a: number): number {
  a = (a + 0x9e3779b9) | 0;
  let t = a ^ (a >>> 16);
  t = Math.imul(t, 0x21f0aaad);
  t = t ^ (t >>> 15);
  t = Math.imul(t, 0x735a2d97);
  return (t ^ (t >>> 15)) >>> 0;
}

export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Stateless hash for per-cell deterministic noise. Used by rendering for the
 * VARIANT grain and by the sim for position-stable choices that must not
 * consume PRNG state (which would break replay determinism).
 */
export function hash2(x: number, y: number, seed = 0): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ seed;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return h >>> 0;
}

/** hash2 normalised to [0,1). */
export function hash2f(x: number, y: number, seed = 0): number {
  return hash2(x, y, seed) / 4294967296;
}

/** Global RNG for presentation-only randomness (particles, screen shake). */
export const fxRng = new Rng(0xc0ffee);
